import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSEO } from "@/components/common/SEO";
import { Reveal } from "@/components/common/Reveal";
import { Icon } from "@/components/common/Icon";
import { SectionLabel } from "@/pages/Home";
import { PRODUCTS } from "@/data/products";
import { PRODUCT_EXTRA, PRODUCT_FAQS } from "@/data/productsExtra";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function ProductDetail() {
  const { lang, t, ml } = useI18n();
  const { slug } = useParams();
  const L = (p) => `/${lang}${p}`;
  const product = PRODUCTS.find((p) => p.slug === slug);
  const extra = PRODUCT_EXTRA[slug] || { gallery: [] };

  useSEO({
    lang,
    title: product ? `${product.name} | HO-SYS` : "HO-SYS",
    description: product ? ml(product.tagline) : "",
    path: `/products/${slug}`,
    image: extra.image,
  });

  if (!product) return <Navigate to={L("/products")} replace />;
  const accent = product.accent;
  const others = PRODUCTS.filter((p) => p.slug !== slug).slice(0, 4);

  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-12">
        <Link to={L("/products")} data-testid="product-back"
          className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> {t("productsPage.label")}
        </Link>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 border" style={{ borderColor: `${accent}66`, background: `${accent}1A` }}>
              <Icon name={product.icon} className="w-7 h-7" style={{ color: accent }} />
            </div>
            <h1 className="font-display font-extrabold tracking-tighter text-white text-4xl sm:text-5xl">{product.name}</h1>
            <p className="mt-3 text-xl text-white/50">{ml(product.tagline)}</p>
            <span className="mt-6 block font-mono text-xs uppercase tracking-widest" style={{ color: accent }}>{t("productsPage.objective")}</span>
            <p className="mt-2 text-white/70 text-lg leading-relaxed">{ml(product.objective)}</p>
            <div className="mt-6 rounded-xl border border-white/10 bg-[#0A0A0A] p-5">
              <span className="font-mono text-xs uppercase tracking-widest text-white/40">{t("productsPage.audience")}</span>
              <p className="mt-1 text-white/75">{ml(product.audience)}</p>
            </div>
            <Link to={L("/contact")} data-testid="product-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold text-black hover:opacity-90 transition-opacity" style={{ background: accent }}>
              {t("nav.demo")} <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.1} className="relative">
            <div className="absolute -inset-6 rounded-3xl blur-3xl opacity-20" style={{ background: accent }} />
            <div className="relative rounded-2xl overflow-hidden border border-white/10">
              <img src={extra.image} alt={product.name} className="w-full h-[380px] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid md:grid-cols-2 gap-12">
        <Reveal>
          <h2 className="font-display text-2xl font-bold text-white mb-6">{t("productsPage.features")}</h2>
          <div className="space-y-3">
            {product.features.map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <ArrowRight className="mt-0.5 w-4 h-4 shrink-0" style={{ color: accent }} />
                <span className="text-white/75">{ml(f)}</span>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="font-display text-2xl font-bold text-white mb-6">{t("productsPage.benefits")}</h2>
          <div className="space-y-3">
            {product.benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-3">
                <Check className="mt-0.5 w-4 h-4 text-lime shrink-0" />
                <span className="text-white/75">{ml(b)}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {extra.gallery?.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 border-t border-white/10">
          <Reveal className="mb-8"><h2 className="font-display text-2xl font-bold text-white">{t("productsPage.gallery")}</h2></Reveal>
          <div className="grid sm:grid-cols-2 gap-5">
            {extra.gallery.map((g, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="rounded-2xl overflow-hidden border border-white/10">
                  <img src={g} alt={`${product.name} ${i + 1}`} loading="lazy" className="w-full h-64 object-cover" />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 border-t border-white/10 grid md:grid-cols-2 gap-12">
        <Reveal>
          <h2 className="font-display text-2xl font-bold text-white mb-4">{t("productsPage.futureTitle")}</h2>
          <p className="text-white/70 text-lg leading-relaxed">{ml(extra.future)}</p>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="font-display text-2xl font-bold text-white mb-4">{t("servicePage.faqTitle")}</h2>
          <Accordion type="single" collapsible className="w-full" data-testid="product-faq">
            {PRODUCT_FAQS.map((f, i) => (
              <AccordionItem key={i} value={`pfaq-${i}`} className="border-white/10">
                <AccordionTrigger data-testid={`product-faq-trigger-${i}`} className="text-left text-white hover:text-neon hover:no-underline">{ml(f.q)}</AccordionTrigger>
                <AccordionContent className="text-white/60">{ml(f.a)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 border-t border-white/10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {others.map((p) => (
            <Link key={p.slug} to={L(`/products/${p.slug}`)} data-testid={`other-product-${p.slug}`}
              className="group rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 hover:border-neon/40 transition-colors">
              <Icon name={p.icon} className="w-6 h-6 mb-4" style={{ color: p.accent }} />
              <h3 className="font-display font-semibold text-white text-sm">{p.name}</h3>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
